/**
 * Packages & pricing (B4 / SPEC-04 R-08…R-12a) — verified against
 * cms-backend src/packages/{dto,controller,service,pricing}.ts at api-v1.0.
 *
 * Templates: create + read only. Patient packages: create (gate-checked on a
 * completed assessment; explicit items always win over template copy or
 * assessment prefill; unit prices snapshotted), read, edit name/price/
 * discount, add line, edit line (planned count never below delivered).
 * NO route completes, closes or deletes a package — completion is DERIVED
 * (completionSatisfied) from delivered sessions; `status` is stored but
 * inert ("active" forever) pending ZOY-244.
 */

import { apiRequest } from '../api/http';

export const DISCOUNT_TYPES = ['percentage', 'fixed'] as const;
export type DiscountType = (typeof DISCOUNT_TYPES)[number];
/**
 * Product 2026-09-06: how the package expects to be paid. `upfront_expected`
 * never blocks — booking and delivery merely WARN while earmarked receipts
 * are below the package total.
 */
export const PAYMENT_TERMS = ['per_session', 'upfront_expected'] as const;
export type PaymentTerms = (typeof PAYMENT_TERMS)[number];

export interface PackageItemInput {
  serviceId: string;
  plannedSessions: number;
  /** Omitted = snapshot the current catalogue price. */
  unitPrice?: number;
}

export interface TemplateView {
  id: string;
  name: string;
  description: string | null;
  /** Decimal on the wire (string) or null. */
  price: string | number | null;
  paymentTerms: PaymentTerms;
  items: { serviceId: string; sessionCount: number; service: { name: string } }[];
}

export const listTemplates = () =>
  apiRequest<{ items: TemplateView[] }>({ base: 'clinic', path: '/v1/package-templates' });

export const createTemplate = (body: {
  name: string;
  description?: string;
  price?: number;
  paymentTerms?: PaymentTerms;
  items: PackageItemInput[];
}) =>
  apiRequest<TemplateView>({
    base: 'clinic',
    method: 'POST',
    path: '/v1/package-templates',
    body,
  });

export interface SessionAmount {
  lineId: string;
  serviceId: string;
  sessionIndex: number;
  baseAmount: string;
  effectiveAmount: string;
}

export interface PackagePricing {
  gross: string;
  discountType: DiscountType | null;
  discountValue: string | null;
  discountAmount: string;
  total: string;
  sessions: SessionAmount[];
}

export interface PackageItemView {
  id: string;
  serviceId: string;
  serviceName: string;
  plannedSessions: number;
  /** Snapshot unit price, 2-decimal string. */
  unitPrice: string;
}

/**
 * All derived on read (Product 2026-09-06): `remainingToSchedule` never goes
 * negative; overflow is reported separately as `overScheduled` (bookings
 * beyond the plan, re-opened voided ones included) and `overDelivered`
 * (effective deliveries beyond the plan — allowed, catalogue-priced).
 * `satisfied` stays true past the plan. Rendered, never recomputed.
 */
export interface PackageProgressRow {
  itemId: string;
  serviceId: string;
  plannedSessions: number;
  scheduledSessions: number;
  deliveredSessions: number;
  remainingToSchedule: number;
  overScheduled: number;
  overDelivered: number;
  satisfied: boolean;
}

/** The package's money picture, all derived by the backend from the patient account. */
export interface PackageFunding {
  paymentTerms: PaymentTerms;
  packageTotal: string;
  earmarkedReceived: string;
  earmarkedApplied: string;
  earmarkedCredit: string;
  /** Σ payableAmount / paidAmount of the package's effective sessions. */
  chargedToDate: string;
  paidToDate: string;
  outstandingAmount: string;
  /** total − earmarkedReceived when upfront is expected, else 0. Warning-only. */
  upfrontShortfall: string;
}

/** What raising `plannedSessions` did to the money — delivered history never moves. */
export interface PackageRepricing {
  plannedBefore: number;
  plannedAfter: number;
  deliveredFrozen: number;
  undeliveredSlotsRepriced: number;
  overPlanDeliveriesNowWithinPlan: number;
  lineAmountsBefore: string[];
  lineAmountsAfter: string[];
  packageTotalBefore: string;
  packageTotalAfter: string;
}

/** Shape of create (pricing only — no progress yet on the create response). */
export interface PatientPackageCreated {
  id: string;
  patientId: string;
  templateId: string | null;
  assessmentId: string | null;
  name: string;
  /** 'active', or 'stopped' by a treatment ending (ZOY-720) — completion stays derived, never read from here. */
  status: string;
  createdBy: string;
  items: PackageItemView[];
  pricing: PackagePricing;
}

export interface PatientPackageView extends PatientPackageCreated {
  progress: PackageProgressRow[];
  /** Derived live: every planned count satisfied by DELIVERED sessions. */
  completionSatisfied: boolean;
  paymentTerms: PaymentTerms;
  funding: PackageFunding | null;
}

export const PackageErrorCode = {
  TemplateNotFound: 'PACKAGE_TEMPLATE_NOT_FOUND',
  NotFound: 'PACKAGE_NOT_FOUND',
  ItemNotFound: 'PACKAGE_ITEM_NOT_FOUND',
  CompositionRequired: 'PACKAGE_COMPOSITION_REQUIRED',
  ServiceNotFound: 'PACKAGE_SERVICE_NOT_FOUND',
  ServiceDisabled: 'PACKAGE_SERVICE_DISABLED',
  /** ZOY-265/ZOY-358: a null catalogue price is never an implicit zero. */
  ServicePriceNotConfigured: 'PACKAGE_SERVICE_PRICE_NOT_CONFIGURED',
  DuplicateService: 'PACKAGE_SERVICE_ALREADY_IN_PACKAGE',
  DiscountInvalid: 'PACKAGE_DISCOUNT_INVALID',
  DiscountExceedsTotal: 'PACKAGE_DISCOUNT_EXCEEDS_TOTAL',
  PlannedBelowDelivered: 'PACKAGE_PLANNED_BELOW_DELIVERED',
} as const;

export const createPatientPackage = (
  patientId: string,
  body: {
    templateId?: string;
    fromAssessmentId?: string;
    name?: string;
    items?: PackageItemInput[];
    price?: number;
    discountType?: DiscountType;
    discountValue?: number;
    /** Omitted = the template's terms, else per_session. */
    paymentTerms?: PaymentTerms;
  },
) =>
  apiRequest<PatientPackageCreated>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/packages`,
    body,
  });

export const listPatientPackages = (patientId: string) =>
  apiRequest<{ items: PatientPackageView[] }>({
    base: 'clinic',
    path: `/v1/patients/${patientId}/packages`,
  });

export const fetchPatientPackage = (id: string) =>
  apiRequest<PatientPackageView>({ base: 'clinic', path: `/v1/patient-packages/${id}` });

export const patchPatientPackage = (
  id: string,
  body: {
    name?: string;
    price?: number | null;
    discountType?: DiscountType | null;
    discountValue?: number | null;
    paymentTerms?: PaymentTerms;
  },
) =>
  apiRequest<PatientPackageView>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/patient-packages/${id}`,
    body,
  });

export const addPackageItem = (packageId: string, body: PackageItemInput) =>
  apiRequest<PackageItemView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patient-packages/${packageId}/items`,
    body,
  });

export const patchPackageItem = (
  itemId: string,
  body: { plannedSessions?: number; unitPrice?: number },
) =>
  apiRequest<PackageItemView & { repricing: PackageRepricing | null }>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/patient-packages/items/${itemId}`,
    body,
  });
